import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "../../config";
import LoginChecked from "../Shared/LoginChecked";
import {
  Table,
  Pagination,
  Row,
  Col,
  Tooltip,
  Button,
  Input,
  message,
  Popconfirm,
  Space,
} from "antd";
import { NavLink } from "react-router-dom";
import {
  UndoOutlined,
  SearchOutlined,
  ArrowLeftOutlined,
  ProfileOutlined,
} from "@ant-design/icons";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";

function DeletedAccounts() {
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");

  const [listofAccounts, setListofAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderby, setOrderBy] = useState("1");
  const [searchedName, setSearchedName] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);

  const fetchDeletedAccounts = () => {
    setLoading(true);
    const data = {
      UserID: UserID,
      AccessKey: AccessKey,
      OrderBy: orderby,
      PageNo: pageNo,
      PageSize: pageSize,
      Name: searchedName,
    };

    axios({
      method: "post",
      url: config.base_url + "account/GetDeletedAccounts",
      headers: { "Content-Type": "application/json" },
      data,
    })
      .then((response) => {
        setLoading(false);
        if (response.data.status_code === 1) {
          setListofAccounts(response.data.listofAccounts || []);
          setTotalRecords(response.data.totalRecords || 0);
        } else {
          message.error(response.data.status_message || "Unable to load deleted accounts.");
        }
      })
      .catch(() => {
        setLoading(false);
        message.error("Unable to load deleted accounts.");
      });
  };

  useEffect(() => {
    document.title = "Deleted Accounts";
    fetchDeletedAccounts();
  }, [orderby, pageNo, pageSize, searchedName]);

  const handleRestore = (accountId) => {
    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "Account/RestoreAccount",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID: UserID,
        AccessKey: AccessKey,
        ID: accountId,
      },
    })
      .then((response) => {
        setLoading(false);
        if (response.data.status_code === 1) {
          message.success(response.data.status_message);
          fetchDeletedAccounts();
        } else {
          message.error(response.data.status_message || "Failed to restore account.");
        }
      })
      .catch(() => {
        setLoading(false);
        message.error("Failed to restore account.");
      });
  };

  const columns = [
    {
      title: "SR",
      dataIndex: "Sr",
      key: "Sr",
      sorter: true,
      render: (_, record, index) => index + 1 + pageSize * (pageNo - 1),
    },
    {
      title: "Name",
      dataIndex: "Name",
      key: "name",
      sorter: true,
    },
    {
      title: "Mobile",
      dataIndex: "Mobile_No",
      key: "Mobile_No",
    },
    {
      title: "Account Type",
      dataIndex: "Account_Type_BE",
      key: "Account_Type_BE",
      render: (accountTypeBE) => (accountTypeBE ? accountTypeBE.Name : ""),
    },
    {
      title: "Deleted On",
      dataIndex: "Updated_Date",
      key: "Updated_Date",
      sorter: true,
      render: (value) => (value ? dayjs(value).format("DD-MM-YYYY HH:mm") : ""),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View Ledger">
            <NavLink to={`/accounts/account-ledger/?account_id=${record.ID}`}>
              <Button type="link" icon={<ProfileOutlined />}>
                View Ledger
              </Button>
            </NavLink>
          </Tooltip>
          <Popconfirm
            title="Restore this account?"
            description="The account will become active again on Manage Accounts."
            onConfirm={() => handleRestore(record.ID)}
          >
            <Tooltip title="Restore account">
              <Button type="link" icon={<UndoOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    const sortField = sorter.field;
    const sortOrder = sorter.order === "descend" ? "desc" : "asc";

    if (sortField === "Name") {
      setOrderBy(sortOrder === "asc" ? "1" : "2");
    } else if (sortField === "Updated_Date") {
      setOrderBy(sortOrder === "asc" ? "5" : "6");
    }
  };

  const onSearch = () => {
    setPageNo(1);
    setSearchedName(searchInput.trim());
  };

  return (
    <>
      <LoginChecked />
      <div className="right-side-contents">
        <div className="page-content">
          <div className="page-header">
            <h3 className="page-title">
              <NavLink to="/accounts/manage-accounts">
                <ChevronLeftIcon />
              </NavLink>
              Deleted Accounts
            </h3>
            <div className="header-actions">
              <NavLink to="/accounts/manage-accounts">
                <Button icon={<ArrowLeftOutlined />}>Back to Manage Accounts</Button>
              </NavLink>
            </div>
          </div>

          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col md={8} xs={24}>
              <Space.Compact style={{ width: "100%" }}>
                <Input
                  placeholder="Search by name"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onPressEnter={onSearch}
                  allowClear
                />
                <Button type="primary" icon={<SearchOutlined />} onClick={onSearch}>
                  Search
                </Button>
              </Space.Compact>
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={listofAccounts}
            loading={loading}
            pagination={false}
            rowKey="ID"
            onChange={handleTableChange}
          />

          <Pagination
            style={{ marginTop: 16, textAlign: "right" }}
            current={pageNo}
            pageSize={pageSize}
            total={totalRecords}
            showSizeChanger
            onChange={(page, size) => {
              setPageNo(page);
              setPageSize(size);
            }}
            onShowSizeChange={(_, size) => {
              setPageNo(1);
              setPageSize(size);
            }}
          />
        </div>
      </div>
    </>
  );
}

export default DeletedAccounts;
